import { getPillarDisplayName } from '../constants/awardCategoryPillar';
import { apiRequest } from './apiConfig';
import { getJudgingDataForCategory } from './vistaJudging';

const currentYear = new Date().getFullYear(); // Default to current year, because cannot evaluate past years

export const getFinalSubmissions = async (
  year: number
): Promise<{ data: any[] }> => {
  const startDate = `${year}-01-01T00:00:00.000Z`;
  const endDate = `${year}-12-31T23:59:59.999Z`;
  const yearFilter =
    `&filters[createdAt][$gte]=${encodeURIComponent(startDate)}` +
    `&filters[createdAt][$lte]=${encodeURIComponent(endDate)}`;

  let allData = [];
  let page = 1;
  let hasMorePages = true;
  const pageSize = 100; // Maximum recommended page size

  while (hasMorePages) {
    const response = await apiRequest(
      `/award-submissions?filters[competition_stage][$eq]=final&populate=*&pagination[page]=${page}&pagination[pageSize]=${pageSize}&pagination[withCount]=true${yearFilter}`,
      {
        method: 'GET',
      }
    );

    const result = await response.json();

    if (!result.data || !Array.isArray(result.data)) {
      throw new Error(
        `Invalid award-submissions data received from API (page ${page})`
      );
    }

    // Add current page data to total
    allData = allData.concat(result.data);

    // Check pagination metadata for more pages
    const pagination = result.meta?.pagination;
    if (pagination) {
      hasMorePages = page < pagination.pageCount;
    } else {
      hasMorePages = false;
    }

    page++;
  }

  const result = { data: allData };

  // Transform pillar names to display names
  if (result.data && Array.isArray(result.data)) {
    result.data = result.data.map(item => ({
      ...item,
      pillar: getPillarDisplayName(item.pillar),
    }));
  }

  return result;
};

/**
 * Submits an evaluation for a final submission using vista-judgment endpoint.
 * This follows the same pattern as qualification evaluation but for final stage.
 *
 * @param {object} evaluationData - The evaluation data containing submissionId and scores.
 * @param {object} judgeInfo - Judge information from auth context: { name, email }
 * @returns {Promise<object>} The response from the vista-judgment API.
 */
export const submitFinalEvaluation = async (evaluationData, judgeInfo) => {
  try {
    // Validate judge information
    if (!judgeInfo || !judgeInfo.email) {
      throw new Error('Judge information is required but was not provided');
    }

    // 1. First, fetch the submission to get its category and details
    const submissionResponse = await apiRequest(
      `/award-submissions/${evaluationData.submissionId}`,
      {
        method: 'GET',
      }
    );

    if (!submissionResponse.ok) {
      throw new Error(
        `Failed to fetch submission: ${submissionResponse.statusText}`
      );
    }

    const submissionResult = await submissionResponse.json();
    const submission = submissionResult.data;

    if (!submission) {
      throw new Error('Submission not found');
    }

    // 2. Get judging data for the submission's category (rubric and aspects)
    const judgingData = await getJudgingDataForCategory(
      submission.category,
      currentYear
    );

    if (!judgingData || !judgingData.rubric || !judgingData.aspects) {
      throw new Error(
        `No judging data found for category: ${submission.category}`
      );
    }

    // 3. Map the manual scores to the rubric aspects
    const aspectScores = [];

    // Map scores to aspects using the criteria order
    Object.entries(evaluationData.scores).forEach(([criteriaKey, score]) => {
      // Extract the criteria index (criteria1 -> 0, criteria2 -> 1, etc.)
      const criteriaMatch = criteriaKey.match(/criteria(\d+)/);
      if (!criteriaMatch) {
        console.warn(`⚠️ Invalid criteria key format: ${criteriaKey}`);
        return;
      }

      const criteriaIndex = parseInt(criteriaMatch[1]) - 1; // Convert to 0-based index
      const aspect = judgingData.aspects[criteriaIndex];

      if (aspect) {
        const aspectScore = {
          aspect_id: aspect.documentId,
          value: score,
          comment: `Manual final evaluation score for ${submission.category} - ${aspect.name}: ${score}/10`,
        };
        aspectScores.push(aspectScore);
      }
    });

    if (aspectScores.length === 0) {
      throw new Error('No valid aspect scores could be mapped to the rubric');
    }

    // 4. Calculate weighted total score using rubric weights
    let totalScore = 0;
    let totalWeight = 0;

    aspectScores.forEach(aspectScore => {
      const aspect = judgingData.aspects.find(
        a => a.documentId === aspectScore.aspect_id
      );
      if (aspect && aspect.weight) {
        // Use the actual rubric weights
        totalScore += aspectScore.value * (aspect.weight / 100); // Convert percentage to decimal
        totalWeight += aspect.weight / 100;
      } else {
        // Equal weight fallback
        totalScore += aspectScore.value;
        totalWeight += 1;
      }
    });

    // Calculate final weighted average
    const averageScore =
      totalWeight > 0
        ? totalScore / totalWeight
        : totalScore / aspectScores.length;

    // 5. Prepare the judgment data for submission (matching vistaJudging.js structure)
    const judgmentData = {
      submission_document_id: submission.documentId,
      submission_table: 'award-submission',
      rubric_document_id: judgingData.rubric.documentId,
      aspect_scores: aspectScores,
      overall_summary: 'Completed Final',
      judge_name: judgeInfo.name,
      judge_email: judgeInfo.email,
      judged_at: new Date().toISOString(),
    };

    // Submit to vista-judgments endpoint
    const response = await apiRequest('/vista-judgments', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ data: judgmentData }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `Vista-judgments submission failed: ${response.statusText} - ${errorText}`
      );
    }

    const result = await response.json();

    return {
      success: true,
      submission: submission,
      judgment: result,
      totalScore: averageScore,
      judgeInfo: judgeInfo,
    };
  } catch (error) {
    console.error('❌ Error in submitFinalEvaluation:', {
      error: error.message,
      stack: error.stack,
      evaluationData,
      judgeInfo,
    });

    return {
      success: false,
      error: error.message,
      evaluationData,
      judgeInfo,
    };
  }
};

/**
 * Submits an evaluation as draft for a final submission
 * @param {object} evaluationData - The evaluation data containing submissionId and scores
 * @param {object} judgeInfo - Judge information from auth context: { name, email }
 * @returns {Promise<object>} The response from the vista-judgment API
 */
export const submitFinalEvaluationAsDraft = async (
  evaluationData,
  judgeInfo
) => {
  try {
    // Validate judge information
    if (!judgeInfo || !judgeInfo.email) {
      throw new Error('Judge information is required but was not provided');
    }

    // 1. First, fetch the submission to get its category and details
    const submissionResponse = await apiRequest(
      `/award-submissions/${evaluationData.submissionId}`,
      {
        method: 'GET',
      }
    );

    if (!submissionResponse.ok) {
      throw new Error(
        `Failed to fetch submission: ${submissionResponse.statusText}`
      );
    }

    const submissionResult = await submissionResponse.json();
    const submission = submissionResult.data;

    if (!submission) {
      throw new Error('Submission not found');
    }

    // 2. Get judging data for the submission's category (rubric and aspects)
    const judgingData = await getJudgingDataForCategory(
      submission.category,
      currentYear
    );

    if (!judgingData || !judgingData.rubric || !judgingData.aspects) {
      throw new Error(
        `No judging data found for category: ${submission.category}`
      );
    }

    // 3. Map the manual scores to the rubric aspects
    const aspectScores = [];

    Object.entries(evaluationData.scores).forEach(([criteriaKey, score]) => {
      const criteriaMatch = criteriaKey.match(/criteria(\d+)/);
      if (!criteriaMatch) {
        console.warn(`⚠️ Invalid criteria key format: ${criteriaKey}`);
        return;
      }

      const criteriaIndex = parseInt(criteriaMatch[1]) - 1;
      const aspect = judgingData.aspects[criteriaIndex];

      if (aspect) {
        const aspectScore = {
          aspect_id: aspect.documentId,
          value: score,
          comment: `Draft final evaluation score for ${submission.category} - ${aspect.name}: ${score}/10`,
        };
        aspectScores.push(aspectScore);
      }
    });

    if (aspectScores.length === 0) {
      throw new Error('No valid aspect scores could be mapped to the rubric');
    }

    // 4. Calculate weighted total score
    let totalScore = 0;
    let totalWeight = 0;

    aspectScores.forEach(aspectScore => {
      const aspect = judgingData.aspects.find(
        a => a.documentId === aspectScore.aspect_id
      );
      if (aspect && aspect.weight) {
        totalScore += aspectScore.value * (aspect.weight / 100);
        totalWeight += aspect.weight / 100;
      } else {
        totalScore += aspectScore.value;
        totalWeight += 1;
      }
    });

    const averageScore =
      totalWeight > 0
        ? totalScore / totalWeight
        : totalScore / aspectScores.length;

    // 5. Prepare the judgment data as draft
    const judgmentData = {
      submission_document_id: submission.documentId,
      submission_table: 'award-submission',
      rubric_document_id: judgingData.rubric.documentId,
      aspect_scores: aspectScores,
      overall_summary: 'Draft Final', // Mark as final draft
      judge_name: judgeInfo.name,
      judge_email: judgeInfo.email,
      judged_at: new Date().toISOString(),
    };

    // Submit to vista-judgments endpoint
    const response = await apiRequest('/vista-judgments', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ data: judgmentData }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `Vista-judgments submission failed: ${response.statusText} - ${errorText}`
      );
    }

    const result = await response.json();

    return {
      success: true,
      submission: submission,
      judgment: result,
      totalScore: averageScore,
      judgeInfo: judgeInfo,
      isDraft: true,
    };
  } catch (error) {
    console.error('❌ Error in submitFinalEvaluationAsDraft:', error);

    return {
      success: false,
      error: error.message,
      evaluationData,
      judgeInfo,
    };
  }
};

/**
 * Gets existing judgments for a judge for specific final submissions
 * @param {Array} submissions - Array of submission objects
 * @param {string} judgeEmail - The judge's email address
 * @returns {Promise<Object>} - Object mapping submission IDs to judgment data
 */
export const getExistingFinalJudgments = async (submissions, judgeEmail) => {
  if (!judgeEmail || !Array.isArray(submissions) || submissions.length === 0) {
    return {};
  }

  try {
    // Get submission IDs
    const submissionIds = submissions
      .map(s => s.documentId || s.id)
      .filter(Boolean);

    if (submissionIds.length === 0) {
      return {};
    }

    // Fetch judgments for this judge and these submissions
    const response = await apiRequest(
      `/vista-judgments?filters[judge_email][$eq]=${encodeURIComponent(judgeEmail)}&populate=*&pagination[pageSize]=100`,
      {
        method: 'GET',
      }
    );

    if (!response.ok) {
      console.warn('Failed to fetch vista-judgments:', response.statusText);
      return {};
    }

    const result = await response.json();
    const judgments = result.data || [];

    // Create mapping of submission ID to judgment data
    const judgmentMap = {};

    judgments.forEach(judgment => {
      const submissionId = judgment.submission_document_id;
      if (submissionIds.includes(submissionId)) {
        judgmentMap[submissionId] = {
          documentId: judgment.documentId,
          overall_summary: judgment.overall_summary,
          aspect_scores: judgment.aspect_scores,
          judged_at: judgment.judged_at,
          judge_name: judgment.judge_name,
          judge_email: judgment.judge_email,
        };
      }
    });

    return judgmentMap;
  } catch (error) {
    console.error('❌ Error fetching existing final judgments:', error);
    return {};
  }
};

/**
 * Filters out submissions that have been completed (overall_summary = "Completed")
 * Only shows submissions that are either not evaluated or still in draft status
 * @param {Array} submissions - Array of submission objects
 * @param {Object} existingJudgments - Object mapping submission IDs to judgment data
 * @returns {Array} - Filtered array of submissions (excludes completed ones)
 */
export const filterIncompleteFinalSubmissions = (
  submissions,
  existingJudgments
) => {
  if (!Array.isArray(submissions) || submissions.length === 0) {
    return submissions;
  }

  const filteredSubmissions = submissions.filter(submission => {
    const submissionId = submission.documentId || submission.id;
    const judgment = existingJudgments[submissionId];

    // Include submission if:
    // 1. No judgment exists (not started)
    // 2. Judgment exists but overall_summary is 'Draft Final' or legacy 'Draft' (can be revised)
    // Exclude if overall_summary indicates completion
    if (!judgment) {
      return true; // Not started - include
    }

    // Include drafts (both new and legacy format)
    if (
      judgment.overall_summary === 'Draft Final' ||
      judgment.overall_summary === 'Draft'
    ) {
      return true; // Draft Final / legacy Draft - include for revision
    }

    // Exclude completed submissions ('Completed Final' or legacy 'Completed')
    return false;
  });

  return filteredSubmissions;
};

/**
 * Updates an existing final evaluation using PUT
 * @param {string} judgmentId - The judgment document ID to update
 * @param {object} evaluationData - The evaluation data containing submissionId and scores
 * @param {object} judgeInfo - Judge information from auth context: { name, email }
 * @param {boolean} isDraft - Whether this is a draft (true) or final submission (false)
 * @returns {Promise<object>} The response from the vista-judgment API
 */
export const updateFinalEvaluation = async (
  judgmentId,
  evaluationData,
  judgeInfo,
  isDraft = true
) => {
  try {
    // 1. Fetch existing judgment to get document ID
    const existingResponse = await apiRequest(
      `/vista-judgments/${judgmentId}`,
      {
        method: 'GET',
      }
    );

    if (!existingResponse.ok) {
      throw new Error(
        `Failed to fetch existing judgment: ${existingResponse.statusText}`
      );
    }

    const existingResult = await existingResponse.json();
    const existingData = existingResult.data;

    if (!existingData) {
      throw new Error('Existing judgment not found');
    }

    const documentId = existingData.documentId;

    // 2. Fetch the submission to get its category and details
    const submissionResponse = await apiRequest(
      `/award-submissions/${evaluationData.submissionId}`,
      {
        method: 'GET',
      }
    );

    if (!submissionResponse.ok) {
      throw new Error(
        `Failed to fetch submission: ${submissionResponse.statusText}`
      );
    }

    const submissionResult = await submissionResponse.json();
    const submission = submissionResult.data;

    if (!submission) {
      throw new Error('Submission not found');
    }

    // 3. Get judging data for the submission's category (rubric and aspects)
    const judgingData = await getJudgingDataForCategory(
      submission.category,
      currentYear
    );

    if (!judgingData || !judgingData.rubric || !judgingData.aspects) {
      throw new Error(
        `No judging data found for category: ${submission.category}`
      );
    }

    // 4. Map the manual scores to the rubric aspects
    const aspectScores = [];

    Object.entries(evaluationData.scores).forEach(([criteriaKey, score]) => {
      const criteriaMatch = criteriaKey.match(/criteria(\d+)/);
      if (!criteriaMatch) {
        console.warn(`⚠️ Invalid criteria key format: ${criteriaKey}`);
        return;
      }

      const criteriaIndex = parseInt(criteriaMatch[1]) - 1;
      const aspect = judgingData.aspects[criteriaIndex];

      if (aspect) {
        const aspectScore = {
          aspect_id: aspect.documentId,
          value: score,
          comment: `Updated final evaluation score for ${submission.category} - ${aspect.name}: ${score}/10`,
        };
        aspectScores.push(aspectScore);
      }
    });

    if (aspectScores.length === 0) {
      throw new Error('No valid aspect scores could be mapped to the rubric');
    }

    // 5. Calculate weighted total score
    let totalScore = 0;
    let totalWeight = 0;

    aspectScores.forEach(aspectScore => {
      const aspect = judgingData.aspects.find(
        a => a.documentId === aspectScore.aspect_id
      );
      if (aspect && aspect.weight) {
        totalScore += aspectScore.value * (aspect.weight / 100);
        totalWeight += aspect.weight / 100;
      } else {
        totalScore += aspectScore.value;
        totalWeight += 1;
      }
    });

    const averageScore =
      totalWeight > 0
        ? totalScore / totalWeight
        : totalScore / aspectScores.length;

    // 6. Prepare the updated judgment data
    const judgmentData = {
      submission_document_id: submission.documentId,
      submission_table: 'award-submission',
      rubric_document_id: judgingData.rubric.documentId,
      aspect_scores: aspectScores,
      overall_summary: isDraft ? 'Draft Final' : 'Completed Final',
      judge_name: judgeInfo.name,
      judge_email: judgeInfo.email,
      judged_at: new Date().toISOString(),
    };

    // Update the existing judgment
    const response = await apiRequest(`/vista-judgments/${documentId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ data: judgmentData }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `Vista-judgments update failed: ${response.statusText} - ${errorText}`
      );
    }

    const result = await response.json();

    return {
      success: true,
      submission: submission,
      judgment: result,
      totalScore: averageScore,
      judgeInfo: judgeInfo,
      isDraft: isDraft,
      isUpdate: true,
    };
  } catch (error) {
    console.error('❌ Error in updateFinalEvaluation:', error);

    return {
      success: false,
      error: error.message,
      evaluationData,
      judgeInfo,
    };
  }
};

/**
 * Completes all draft final evaluations for a judge
 * @param {Array} draftJudgmentIds - Array of judgment document IDs that are drafts
 * @param {object} judgeInfo - Judge information from auth context: { name, email }
 * @returns {Promise<object>} The result of completing all evaluations
 */
export const completeAllFinalEvaluations = async (
  draftJudgmentIds,
  judgeInfo
) => {
  try {
    const results = [];
    const errors = [];

    for (let i = 0; i < draftJudgmentIds.length; i++) {
      const judgmentId = draftJudgmentIds[i];
      try {
        // Fetch the existing judgment to get the submission data
        const judgmentResponse = await apiRequest(
          `/vista-judgments/${judgmentId}`,
          {
            method: 'GET',
          }
        );

        if (!judgmentResponse.ok) {
          throw new Error(
            `Failed to fetch judgment ${judgmentId}: ${judgmentResponse.statusText}`
          );
        }

        const judgmentResult = await judgmentResponse.json();
        const judgment = judgmentResult.data;

        if (!judgment) {
          throw new Error(`Judgment ${judgmentId} not found`);
        }

        // Update the judgment to mark as completed final
        const updatedJudgmentData = {
          ...judgment,
          overall_summary: 'Completed Final',
          judge_name: judgeInfo.name,
          judge_email: judgeInfo.email,
          judged_at: new Date().toISOString(),
        };

        // Remove the documentId from the data payload
        delete updatedJudgmentData.documentId;
        delete updatedJudgmentData.id;
        delete updatedJudgmentData.createdAt;
        delete updatedJudgmentData.updatedAt;
        delete updatedJudgmentData.publishedAt;

        const updateResponse = await apiRequest(
          `/vista-judgments/${judgment.documentId}`,
          {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ data: updatedJudgmentData }),
          }
        );

        if (!updateResponse.ok) {
          const errorText = await updateResponse.text();
          throw new Error(
            `Failed to update judgment ${judgmentId}: ${updateResponse.statusText} - ${errorText}`
          );
        }

        const updateResult = await updateResponse.json();
        results.push({
          judgmentId: judgmentId,
          success: true,
          result: updateResult,
        });
      } catch (error) {
        console.error(`❌ Error completing judgment ${judgmentId}:`, error);
        errors.push({
          judgmentId: judgmentId,
          error: error.message,
        });
      }
    }

    return {
      success: errors.length === 0,
      successfulCount: results.length,
      totalProcessed: draftJudgmentIds.length,
      results: results,
      errors: errors,
    };
  } catch (error) {
    console.error('❌ Error in completeAllFinalEvaluations:', error);

    return {
      success: false,
      error: error.message,
      successfulCount: 0,
      totalProcessed: draftJudgmentIds.length,
      results: [],
      errors: [{ error: error.message }],
    };
  }
};
